import * as clipping from 'martinez-polygon-clipping'
import { normalizeFeed, SURF_TIMEZONE, MARINE_FIELDS } from './surf-forecast.mjs'

export const FIELD_MODEL = 'meteofrance_wave'
export const FIELD_STEP = 1 / 12
export const FIELD_POINTS = Object.freeze([-8.875, -8.958333, -9.041667, -9.125].flatMap(latitude => [116.041667, 116.125, 116.208333, 116.291667].map(longitude => ({ latitude, longitude }))))
export const FIELD_FIELDS = Object.fromEntries(Object.entries(MARINE_FIELDS).filter(([key]) => ['wave_height', 'swell_wave_height', 'swell_wave_period'].includes(key)))
export const FIELD_LAYERS = Object.freeze([
  { id: 'swell_height', label: 'Swell height', field: 'swell_wave_height', unit: 'm', max: 4, ticks: [0, 1, 2, 3, 4] },
  { id: 'wave_height', label: 'Total wave height', field: 'wave_height', unit: 'm', max: 4, ticks: [0, 1, 2, 3, 4] },
  { id: 'swell_period', label: 'Swell period', field: 'swell_wave_period', unit: 's', max: 20, ticks: [0, 5, 10, 15, 20] },
])
// Viridis: the same numeric scale is retained for every hour, never auto-scaled.
export const FIELD_COLORS = ['#440154', '#3b528b', '#21918c', '#5ec962', '#fde725']
const finite = value => typeof value === 'number' && Number.isFinite(value)
export function fieldScalePosition(value, layer) {
  if (!finite(value) || value < 0 || !finite(layer?.max) || layer.max <= 0) return null
  return Math.min(1, value / layer.max) * 100
}
export function fieldReferenceReading(field, time, layer, spot) {
  const index = field?.times?.indexOf(time) ?? -1
  if (index < 0 || !finite(time) || !finite(spot?.latitude) || !finite(spot?.longitude)) return null
  const radians = degrees => degrees * Math.PI / 180
  const latitude = radians(spot.latitude)
  let nearest = null, distance = Infinity
  for (const cell of field.cells || []) {
    if (!finite(cell.latitude) || !finite(cell.longitude)) continue
    // Compare great-circle distance to actual returned model-cell centres.
    const dLat = radians(cell.latitude - spot.latitude), dLon = radians(cell.longitude - spot.longitude)
    const next = Math.sin(dLat / 2) ** 2 + Math.cos(latitude) * Math.cos(radians(cell.latitude)) * Math.sin(dLon / 2) ** 2
    if (next < distance || (next === distance && cell.id < nearest?.id)) { nearest = cell; distance = next }
  }
  // A missing nearest-cell value stays unavailable; never borrow a farther cell
  // or a value from the beach summary's potentially different marine model.
  const value = nearest?.values?.[layer?.field]?.[index]
  const position = fieldScalePosition(value, layer)
  return position === null ? null : { cellId: nearest.id, model: field.model, time, value, unit: layer.unit, position, aboveScale: value > layer.max }
}
export function fieldColor(value, layer) {
  const percentage = fieldScalePosition(value, layer)
  if (percentage === null) return null
  const position = percentage / 100 * (FIELD_COLORS.length - 1)
  const left = Math.floor(position), right = Math.min(FIELD_COLORS.length - 1, left + 1), mix = position - left
  const rgb = color => [1, 3, 5].map(offset => parseInt(color.slice(offset, offset + 2), 16))
  const a = rgb(FIELD_COLORS[left]), b = rgb(FIELD_COLORS[right])
  return `rgb(${a.map((v, i) => Math.round(v + (b[i] - v) * mix)).join(',')})`
}
export function buildFieldUrl() {
  return `https://marine-api.open-meteo.com/v1/marine?${new URLSearchParams({ latitude: FIELD_POINTS.map(p => p.latitude).join(','), longitude: FIELD_POINTS.map(p => p.longitude).join(','), hourly: Object.keys(FIELD_FIELDS).join(','), models: FIELD_MODEL, forecast_days: '7', timezone: SURF_TIMEZONE, timeformat: 'unixtime', cell_selection: 'sea' })}`
}
export function normalizeMarineField(feeds, retrievedAt) {
  if (!Array.isArray(feeds) || feeds.length !== FIELD_POINTS.length) throw new Error('Unexpected marine field locations')
  const cells = new Map()
  const normalized = feeds.map(feed => normalizeFeed(feed, FIELD_FIELDS))
  const times = [...new Set(normalized.flatMap(feed => [...feed.rows.keys()]))].sort((a, b) => a - b)
  if (times.length > 192) throw new Error('Unexpected marine field horizon')
  normalized.forEach((feed, index) => {
    const { latitude, longitude } = feed.grid
    if (!finite(latitude) || !finite(longitude) || Math.abs(latitude - FIELD_POINTS[index].latitude) > FIELD_STEP / 2 + .001 || Math.abs(longitude - FIELD_POINTS[index].longitude) > FIELD_STEP / 2 + .001) throw new Error('Unexpected marine model grid')
    const id = `${latitude.toFixed(4)}:${longitude.toFixed(4)}`
    // Deduplicate actual returned cells. Repeated samples never invent detail.
    if (!cells.has(id)) cells.set(id, { id, latitude, longitude, values: Object.fromEntries(Object.keys(FIELD_FIELDS).map(key => [key, times.map(time => feed.rows.get(time)?.[key] ?? null)])) })
  })
  if (![...cells.values()].some(cell => cell.values.wave_height.some(finite))) throw new Error('Marine field unavailable')
  return { model: FIELD_MODEL, resolutionKm: 8, step: FIELD_STEP, retrievedAt: new Date(retrievedAt).toISOString(), times, cells: [...cells.values()] }
}
export function cellRing(cell, step = FIELD_STEP) {
  const h = step / 2
  return [[cell.longitude - h, cell.latitude - h], [cell.longitude + h, cell.latitude - h], [cell.longitude + h, cell.latitude + h], [cell.longitude - h, cell.latitude + h], [cell.longitude - h, cell.latitude - h]]
}
const intersection = clipping.intersection || clipping.default?.intersection
export function clipFieldToWater(cells, waterFeatures, step = FIELD_STEP) {
  const water = new Map()
  for (const feature of waterFeatures || []) {
    const g = feature?.geometry
    if (g?.type === 'Polygon' || g?.type === 'MultiPolygon') water.set(JSON.stringify(g), g.type === 'Polygon' ? [g.coordinates] : g.coordinates)
  }
  if (!water.size) return [] // Never fall back to unmasked marine rectangles.
  return cells.flatMap(cell => {
    const pieces = []
    for (const polygons of water.values()) {
      try {
        const result = intersection([[cellRing(cell, step)]], polygons)
        if (result?.length) pieces.push(...result)
      } catch { /* A malformed tile omits coverage, never paints unmasked land. */ }
    }
    return pieces.length ? [{ type: 'Feature', id: cell.id, properties: { cellId: cell.id }, geometry: { type: 'MultiPolygon', coordinates: pieces } }] : []
  })
}
export function fieldGeoJson(field, time, layer, waterGeometry) {
  const index = field?.times.indexOf(time) ?? -1
  const byId = new Map(field?.cells.map(cell => [cell.id, cell]) || [])
  return { type: 'FeatureCollection', features: index < 0 ? [] : waterGeometry.flatMap(feature => {
    const value = byId.get(feature.properties.cellId)?.values[layer.field]?.[index]
    const color = fieldColor(value, layer)
    return color ? [{ ...feature, properties: { ...feature.properties, value, color } }] : []
  }) }
}
export function timeStepIndex(length, index, key) {
  if (!Number.isInteger(length) || length < 1) return null
  const requested = ({ ArrowRight: index + 1, ArrowLeft: index - 1, PageDown: index + 24, PageUp: index - 24, Home: 0, End: length - 1 })[key]
  return Number.isInteger(requested) ? Math.max(0, Math.min(length - 1, requested)) : null
}
